import React, { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, ConfigProvider, Descriptions, Form, Input, Modal, Space, Spin, Tag, Typography } from "antd";
import container from "../../../../../lib/di/container";
import AuthRepo from "../../../../../core/repos/auth.repo";
import { useFactoryRuntimeSettings } from "../../../../../core/stores/factory-runtime-settings.store";
import { ElectronicContractRepo } from "../../../../../core/repos/tables/electronic-contract.repo";
import { MODAL_THEME } from "../../../../../core/themes/modal.theme";
import { useConfigurationPermissions } from "@/core/hooks/useConfigurationPermissions";
import type { MatBaoConnectionSettings, UpdateMatBaoConnectionSettings } from "../../../../../core/models/matbao-connection-settings.model";

const authRepo = container.get(AuthRepo);
const electronicContractRepo = container.get(ElectronicContractRepo);

interface MatBaoConnectionSettingsModalProps {
  isModalOpen: boolean;
  setIsModalOpen?: Dispatch<SetStateAction<boolean>>;
  inline?: boolean;
  onSuccess?: () => void;
}

export const MatBaoConnectionSettingsModal: React.FC<MatBaoConnectionSettingsModalProps> = props => {
  const permissions=useConfigurationPermissions();
  return permissions.access.contracts.view ? <AdminMatBaoSettingsModal {...props} permission={permissions} key={permissions.sessionKey}/> : null;
};

const AdminMatBaoSettingsModal: React.FC<MatBaoConnectionSettingsModalProps&{permission:ReturnType<typeof useConfigurationPermissions>}> = ({ isModalOpen, setIsModalOpen, inline = false, onSuccess,permission }) => {
  const [form] = Form.useForm<UpdateMatBaoConnectionSettings>();
  const user = authRepo.useDetails();
  const runtime = useFactoryRuntimeSettings();
  const [settings, setSettings] = useState<MatBaoConnectionSettings | null>(null);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState<"test" | "save" | null>(null);
  const [error, setError] = useState("");
  const [result, setResult] = useState<{ type: "success" | "error"; message: string; description?: string } | null>(null);
  const requestVersion = useRef(0);
  const actionInFlight = useRef(false);
  const buildEnvironment = process.env.REACT_APP_ECONTRACT_ENVIRONMENT;
  const environment = buildEnvironment === "uat" || buildEnvironment === "production" ? buildEnvironment
    : runtime.factoryId === user?.waterFactoryId ? runtime.environment : null;
  const environmentMatches = (environment === "uat" || environment === "production") && settings?.environment === environment;
  const writable = permission.access.contracts.edit && !!settings?.canEdit && environmentMatches;
  const busy = loading || !!working;

  const loadSettings = useCallback(async () => {
    const permitted=permission.guard('contracts','view');if(!permitted())return;
    const version = ++requestVersion.current;
    setLoading(true);
    setSettings(null);
    setError("");
    setResult(null);
    form.resetFields();
    try {
      const response = await electronicContractRepo.getConnectionSettings();
      if (version !== requestVersion.current||!permitted()) return;
      if (response?.code !== "00" || !response.result) {
        setError(response?.errorMessage || "Không tải được cấu hình Mắt Bão. Vui lòng thử lại.");
        return;
      }
      setSettings(response.result);
      form.setFieldsValue({ taxCode: response.result.taxCode || "", username: response.result.username || "", password: "", companyEmail: response.result.companyEmail || "" });
    } catch {
      if (version === requestVersion.current) setError("Không tải được cấu hình Mắt Bão. Vui lòng kiểm tra kết nối.");
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  }, [form,permission.guard]);

  useEffect(() => {
    if (isModalOpen) void loadSettings();
    else {
      ++requestVersion.current;
      form.resetFields();
      setSettings(null);
      setResult(null);
      setError("");
      setLoading(false);
      setWorking(null);
      actionInFlight.current = false;
    }
    // Invalidate every pending request; this ref is a request counter, not a DOM node.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => { ++requestVersion.current; };
  }, [isModalOpen, loadSettings, form]);

  const close = () => {
    if (actionInFlight.current) return;
    ++requestVersion.current;
    form.setFieldsValue({ password: "" });
    setResult(null);
    setError("");
    setIsModalOpen?.(false);
  };

  const submit = async (action: "test" | "save") => {
    const permitted=permission.guard('contracts','edit');
    if (!writable || !settings || busy || actionInFlight.current||!permitted()) return;
    actionInFlight.current = true;
    const version = requestVersion.current;
    setWorking(action);
    let input: UpdateMatBaoConnectionSettings;
    try { input = await form.validateFields(); } catch {
      if (version === requestVersion.current) {
        actionInFlight.current = false;
        setWorking(null);
      }
      return;
    }
    if (version !== requestVersion.current||!permitted()) return;
    const model: UpdateMatBaoConnectionSettings = {
      taxCode: input.taxCode?.trim(), username: input.username.trim(), password: input.password || "", companyEmail: input.companyEmail.trim(), revision: settings.revision,
    };
    setError("");
    setResult(null);
    try {
      if (action === "test") {
        const response = await electronicContractRepo.testConnectionSettings(model);
        if (version !== requestVersion.current||!permitted()) return;
        if (response?.code !== "00" || !response.result) {
          setError(response?.errorMessage || "Không kiểm tra được kết nối. Vui lòng thử lại.");
          return;
        }
        const check = response.result;
        setResult({ type: check.success ? "success" : "error", message: check.message,
          description: check.success ? `Đã đọc được ${check.templateCount} mẫu hợp đồng từ Mắt Bão.` : undefined });
      } else {
        const response = await electronicContractRepo.saveConnectionSettings(model);
        if (version !== requestVersion.current||!permitted()) return;
        if (response?.code !== "00" || !response.result) {
          setError(response?.errorMessage || "Chưa xác định được kết quả lưu. Tải lại cấu hình trước khi thử tiếp.");
          return;
        }
        setSettings(response.result);
        form.setFieldsValue({ taxCode: response.result.taxCode || "", username: response.result.username || "", password: "", companyEmail: response.result.companyEmail || "" });
        setResult({ type: "success", message: "Đã lưu cấu hình Mắt Bão." });
        onSuccess?.();
      }
    } catch {
      if (version === requestVersion.current) setError(action === "test"
        ? "Không kiểm tra được kết nối. Vui lòng thử lại."
        : "Chưa xác định được kết quả lưu. Tải lại cấu hình trước khi thử tiếp.");
    } finally {
      if (version === requestVersion.current) {
        actionInFlight.current = false;
        setWorking(null);
      }
    }
  };

  const actions = <Space wrap>
        {!inline && <Button onClick={close} disabled={!!working}>Đóng</Button>}
        <Button onClick={() => void submit("test")} loading={working === "test"} disabled={!writable || busy}>Kiểm tra kết nối</Button>
        <Button type="primary" onClick={() => void submit("save")} loading={working === "save"} disabled={!writable || busy}>Lưu cấu hình</Button>
      </Space>;
  const content = <Space direction="vertical" size="middle" style={{ width: "100%" }}>
        <Typography.Paragraph style={{ marginBottom: 0 }}>Tài khoản và email ký công ty chỉ áp dụng cho đơn vị trong môi trường đang đăng nhập. UAT và production lưu cấu hình riêng.</Typography.Paragraph>
        {error && <Alert type="error" showIcon message={error} />}
        {result && <Alert type={result.type} showIcon message={result.message} description={result.description} />}
        <Button onClick={() => void loadSettings()} loading={loading} disabled={busy}>Tải lại cấu hình</Button>
        <Spin spinning={loading}>
          <Form form={form} layout="vertical" disabled={!writable || busy} autoComplete="off"
            onValuesChange={() => { setError(""); setResult(null); }}>
          {settings && <>
            <Descriptions size="small" column={1} bordered className="mb-4">
              <Descriptions.Item label="Môi trường"><Tag color={settings.environment === "production" ? "green" : "orange"}>{settings.environment === "production" ? "Production" : "UAT / Demo"}</Tag></Descriptions.Item>
              <Descriptions.Item label="API Mắt Bão">{settings.baseUrl}</Descriptions.Item>
            </Descriptions>
            {!environmentMatches && <Alert type="error" showIcon className="mb-4" message="Môi trường web và API không khớp. Không thể sửa cấu hình; vui lòng liên hệ quản trị hệ thống." />}
            {environmentMatches && !settings.canEdit && <Alert type="warning" showIcon className="mb-4" message="Máy chủ chưa cho phép cập nhật cấu hình Mắt Bão trên web." />}
              <Form.Item name="taxCode" label="Mã số thuế Mắt Bão" rules={[
                { required: true, whitespace: true, message: "Nhập mã số thuế Mắt Bão." },
                { validator: (_, value) => !value || /^[A-Za-z0-9-]{1,32}$/.test(value.trim())
                  ? Promise.resolve() : Promise.reject(new Error("Mã số thuế chỉ gồm chữ, số hoặc dấu gạch ngang; tối đa 32 ký tự.")) },
              ]}>
                <Input maxLength={32} autoComplete="off" />
              </Form.Item>
              <Form.Item name="username" label="Tài khoản Mắt Bão" rules={[
                { required: true, whitespace: true, message: "Nhập tài khoản Mắt Bão." },
                { max: 256, message: "Tài khoản tối đa 256 ký tự." },
              ]}>
                <Input maxLength={256} autoComplete="off" />
              </Form.Item>
              <Form.Item name="password" label="Mật khẩu Mắt Bão" dependencies={["username", "taxCode"]}
                extra={settings.hasPassword ? "Đã có mật khẩu. Để trống để giữ nguyên; đổi tài khoản hoặc mã số thuế cần nhập mật khẩu mới." : "Chưa có mật khẩu. Nhập mật khẩu để kiểm tra và lưu cấu hình."}
                rules={[{ max: 4096, message: "Mật khẩu tối đa 4096 ký tự." }, ({ getFieldValue }) => ({ validator(_, value) {
                  const needsPassword = !settings.hasPassword || getFieldValue("username")?.trim() !== settings.username
                    || getFieldValue("taxCode")?.trim() !== settings.taxCode;
                  return needsPassword && !value ? Promise.reject(new Error("Nhập mật khẩu mới khi đổi tài khoản, mã số thuế hoặc chưa có mật khẩu.")) : Promise.resolve();
                } })]}>
                <Input.Password visibilityToggle={false} maxLength={4096} autoComplete="new-password" />
              </Form.Item>
              <Form.Item name="companyEmail" label="Email ký công ty" rules={[
                { required: true, whitespace: true, message: "Nhập email ký công ty." },
                { max: 254, message: "Email tối đa 254 ký tự." },
                { validator: (_, value) => !value || /^[^\s<>@]+@[^\s<>@]+\.[^\s<>@]+$/.test(value.trim())
                  ? Promise.resolve() : Promise.reject(new Error("Nhập một địa chỉ email hợp lệ.")) },
              ]}>
                <Input maxLength={254} autoComplete="off" inputMode="email" />
              </Form.Item>
          </>}
          </Form>
        </Spin>
        <Alert type="info" showIcon message="Kiểm tra kết nối sẽ xác thực tài khoản và đọc danh sách mẫu; không tạo hoặc ký hợp đồng."
          description="Khi lưu, máy chủ kiểm tra kết nối lại trước khi áp dụng. Các hợp đồng đã phát hành giữ nguyên người ký đã lưu." />
      </Space>;

  return <ConfigProvider theme={MODAL_THEME}>
    {inline ? <div style={{ maxWidth: 800 }}>
      {content}
      <div className="mt-4 flex justify-end">{actions}</div>
    </div> : <Modal open={isModalOpen} title="Cấu hình Mắt Bão" width={680} onCancel={close} maskClosable={!working} keyboard={!working} forceRender footer={actions}>
      {content}
    </Modal>}
  </ConfigProvider>;
};
